import { authenticatedFetch } from '../api';

export type CommercialPreview = {
  buyer: {
    company_id: string;
    company_name: string;
    company_location_id: string;
    company_location_name: string;
    active_catalogs: Array<{ id: string; title: string }>;
  };
  product: {
    product_id: string;
    product_title: string;
    variant_id: string;
    variant_title: string;
    sku: string | null;
  };
  quantity: number;
  shopify_current: {
    contextual_price: string;
    effective_unit_price: string;
    currency: string;
    compare_at_price: string | null;
    quantity_rule: {
      minimum: number;
      maximum: number | null;
      increment: number;
      origin: string;
      is_default: boolean;
    };
    quantity_breaks: Array<{
      minimum_quantity: number;
      price: string;
      currency: string;
    }>;
    quantity_violations: string[];
  };
  draft: {
    id: string;
    policy_key: string;
    name: string;
    version: number;
  } | null;
  proposed_overlay: {
    base_price: string;
    final_price: string;
    currency: string;
    min_quantity: number | null;
    max_quantity: number | null;
    quantity_increment: number | null;
    price_source_policy: string | null;
    matched_policies: string[];
    safe_for_this_context: boolean;
    conflicts: Array<{
      family: string;
      priority: number;
      policy_keys: string[];
      message: string;
    }>;
    trace: Array<{
      policy_key: string;
      version_number: number;
      priority: number;
      matched: boolean;
      reason: string;
      applied_actions: string[];
      ignored_actions: string[];
    }>;
    metadata: Record<string, unknown>;
  } | null;
  semantics: string;
};

export async function getCommercialPreview(input: {
  company_location_id: string;
  variant_id: string;
  quantity: number;
  draft_policy_id?: string | null;
}): Promise<CommercialPreview> {
  const response = await authenticatedFetch('/api/preview/commercial', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `Could not build commercial preview (${response.status})`);
  }
  return (await response.json()) as CommercialPreview;
}
